import { Provider, Message, ProviderResponse, FunctionDefinition } from "../providers/types";
import { makeProvider } from "./simpleAgent";
import { AssetLoader } from "../assets/loader";
import { Persona } from "../types/persona";
import kleur from "kleur";
import { toolRegistry, ExecutionContext, ToolResult } from "../tools/registry";
import { registerBuiltinTools } from "../tools/builtin";
import { summarizeRepo } from "../tools/repo";
import { loadConfig } from "../config";
import { getSessionMemory, SessionMemory } from "../runtime/sessionMemory";
import { getAgentMemory, AgentMemoryManager } from "../runtime/agentMemory";
import { MemoryManager, createMemoryManager } from "../runtime/memoryManager";
import { PermissionManager, PermissionMode } from "../permissions";
import * as readline from 'readline';
import { ErrorHandler } from "../errors/errorHandler";
// New feature integrations
import { getHookManager, HookManager } from "../hooks";
import { getCheckpointManager, CheckpointManager } from "../checkpoints";
import { getSkillLoader, SkillLoader, LoadedSkill } from "../skills";
import { getRulesLoader, RulesLoader } from "../runtime/rulesLoader";
import { getPlanModeManager, PlanModeManager } from "../planning";
import { getRosettaLoader, RosettaLoader } from "../runtime/rosettaLoader";
import { RailroadMemory, initRailroadMemory } from "../runtime/railroadMemory";

export interface AgentResult {
  type: "completed" | "failed" | "max_iterations";
  content: string;
  iterations: number;
  toolCalls: number;
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  context: ExecutionContext;
}

export class ToolCallingAgent {
  private provider: Provider;
  private context: ExecutionContext;
  private persona: Persona | null = null;
  private sessionMemory: SessionMemory;
  private agentMemory: AgentMemoryManager;
  private memoryManager: MemoryManager;
  private permissionManager: PermissionManager;
  private thinkingInterval?: NodeJS.Timeout;
  private currentAction: string = '';
  private recentOperations: Map<string, number> = new Map();
  private fileCache: Map<string, { content: string; timestamp: number }> = new Map();
  private completedOperations: Set<string> = new Set();
  private consecutiveNoToolCalls: number = 0;
  private currentTokenUsage: { prompt: number; completion: number; total: number } = { prompt: 0, completion: 0, total: 0 };
  private lastProgressLine: string = '';
  // New feature managers
  private hookManager: HookManager;
  private checkpointManager: CheckpointManager;
  private skillLoader: SkillLoader;
  private rulesLoader: RulesLoader;
  private planModeManager: PlanModeManager;
  private rosettaLoader: RosettaLoader;
  private activeSkills: LoadedSkill[] = [];
  private railroadMemory: RailroadMemory | null = null;

  constructor(sessionId?: string, readlineInterface?: readline.Interface) {
    this.provider = makeProvider();
    
    // Initialize permission system
    const initialMode = process.env.METIS_AUTO_APPROVE === 'true' 
      ? PermissionMode.AUTO_ACCEPT 
      : PermissionMode.NORMAL;
    
    this.permissionManager = new PermissionManager(initialMode, readlineInterface);
    
    this.context = {
      sessionId: sessionId || `session-${Date.now()}`,
      workingDirectory: process.cwd(),
      config: { autoApprove: process.env.METIS_AUTO_APPROVE === 'true' },
      traceEnabled: process.env.METIS_TRACE === 'true',
      verboseEnabled: process.env.METIS_VERBOSE === 'true',
      permissionManager: this.permissionManager,
      clearProgress: () => this.clearProgress()
    };

    // Initialize session memory and agent memory
    this.sessionMemory = getSessionMemory(this.context.workingDirectory);
    this.sessionMemory.loadSession(this.context.sessionId);

    // Initialize hierarchical agent memory
    this.agentMemory = getAgentMemory(this.context.workingDirectory);

    // Initialize advanced memory manager with AI-powered summarization
    this.memoryManager = createMemoryManager(
      this.context.workingDirectory,
      {
        maxContextTokens: 180000, // Conservative limit for most models
        autoCompactThreshold: 0.75, // Auto-compact at 75% capacity
        minMessagesBeforeCompact: 8,
        preserveRecentMessages: 4,
        compressionStrategy: {
          method: 'summarize',
          targetReduction: 0.6,
          preserveRecent: 4,
          preserveImportant: true
        }
      },
      // AI-powered summarization function
      async (messages, context) => {
        try {
          const conversationText = messages.map(m =>
            `${m.role}: ${typeof m.content === 'string' ? m.content : JSON.stringify(m.content)}`
          ).join('\n\n');

          const summaryPrompt = `Create a concise summary of this development session, focusing on:
- Key tasks completed and their outcomes
- Important decisions and changes made
- Files and components that were modified
- Current progress and any pending work
- Technical context that should be preserved

${context ? `Session Context:\n${context}\n\n` : ''}Conversation:
${conversationText}

Summary:`;

          const summary = await this.provider.send([
            { role: 'system', content: 'You create concise, technical summaries of development conversations that preserve important context.' },
            { role: 'user', content: summaryPrompt }
          ]);

          return summary;
        } catch (error: any) {
          console.warn('AI summarization failed:', error.message);
          // Fallback to simple summarization
          const userMessages = messages.filter(m => m.role === 'user').map(m => m.content);
          const taskCount = userMessages.length;
          const recentTasks = userMessages.slice(-3);

          return [
            `Previous conversation included ${taskCount} user requests.`,
            recentTasks.length > 0 ? `Recent tasks: ${recentTasks.join('; ')}` : '',
            context || ''
          ].filter(Boolean).join(' ');
        }
      }
    );

    // Register built-in tools
    registerBuiltinTools();

    // Initialize new feature managers
    this.hookManager = getHookManager(this.context.workingDirectory, this.context.sessionId);
    this.checkpointManager = getCheckpointManager(this.context.workingDirectory, this.context.sessionId);
    this.skillLoader = getSkillLoader(this.context.workingDirectory);
    this.rulesLoader = getRulesLoader(this.context.workingDirectory);
    this.planModeManager = getPlanModeManager(this.context.workingDirectory, this.context.sessionId);
    this.rosettaLoader = getRosettaLoader(this.context.workingDirectory);

    // Initialize Railroad Memory for persistent context
    this.initRailroad();
  }

  /**
   * Initialize Railroad Memory asynchronously
   */
  private async initRailroad(): Promise<void> {
    try {
      this.railroadMemory = await initRailroadMemory(
        this.context.sessionId,
        this.context.workingDirectory
      );
    } catch (error: any) {
      console.warn('Railroad Memory initialization failed:', error.message);
    }
  }

  async executeWithTools(
    task: string,
    availableTools: string[] = [],
    maxIterations = 50  // Much higher limit like Claude Code
  ): Promise<AgentResult> {
    
    // Set current task in session memory
    this.sessionMemory.setCurrentTask(task);
    
    if (this.context.verboseEnabled) {
      console.log(`Session: ${this.context.sessionId}`);
      console.log(`Max steps: ${maxIterations}`);
    }

    // Load persona
    await this.loadPersona();

    // Activate relevant skills for this task
    this.activateSkillsForTask(task);

    // Check if provider supports tools
    if (!this.provider.supportsTools()) {
      // Fallback to simple agent behavior
      const { runSimpleAgent } = await import("./simpleAgent");
      const result = await runSimpleAgent("run", task);
      
      // Save to session memory
      this.sessionMemory.addMessage({ role: "user", content: task });
      this.sessionMemory.addMessage({ role: "assistant", content: result });
      
      return {
        type: "completed",
        content: result,
        iterations: 1,
        toolCalls: 0,
        context: this.context
      };
    }

    const tools = this.getAvailableTools(availableTools);
    const systemPrompt = this.buildSystemPrompt(tools);
    
    // Check if we need to compress memory using the advanced memory manager
    const compressionCheck = this.memoryManager.shouldCompress();
    if (compressionCheck.needed) {
      if (this.context.verboseEnabled) {
        const urgencyIcon = compressionCheck.urgency === 'high' ? '🚨' : compressionCheck.urgency === 'medium' ? '⚠️' : '🗜️';
        console.log(kleur.yellow(`${urgencyIcon} Auto-compressing memory: ${compressionCheck.reason}`));
      }

      try {
        const result = await this.memoryManager.compressMemory();

        if (result.success && this.context.verboseEnabled) {
          const efficiency = ((result.tokensReduced / (result.tokensReduced + this.memoryManager.getMemoryStats().tokenEstimate.total)) * 100).toFixed(1);
          console.log(kleur.green(`✅ Memory compressed: ${result.originalCount}→${result.newCount} messages (~${efficiency}% space saved)`));
        }
      } catch (error: any) {
        if (this.context.verboseEnabled) {
          console.log(kleur.yellow('⚠️  Memory compression failed, continuing...'));
        }
        console.warn('Failed to compress memory:', error.message);
      }
    }

    // Build messages with session context
    let messages: Message[] = this.buildMessagesWithContext(systemPrompt, task);

    let iterations = 0;
    let toolCalls = 0;
    let totalTokens = { prompt: 0, completion: 0, total: 0 };

    mainLoop: for (let i = 0; i < maxIterations; i++) {
      iterations++;
      
      if (this.context.traceEnabled) {
        console.log(`\n--- Iteration ${i + 1}/${maxIterations} ---`);
        console.log("Messages:", messages.length);
      }

      try {
        // Show thinking indicator when sending to AI
        if (i === 0) {
          this.showProgress('Analyzing request');
        } else {
          this.showProgress('Processing');
        }

        const response = await ErrorHandler.withNetworkRetry(
          () => this.provider.sendWithTools(messages, tools, {
            temperature: this.persona?.temperature,
            max_tokens: 16000  // Increased for large file contents and comprehensive responses
          }),
          'AI provider request'
        );

        // Clear progress once we get a response
        this.clearProgress();

        // Accumulate token usage and update display
        if (response.usage) {
          totalTokens.prompt += response.usage.prompt_tokens;
          totalTokens.completion += response.usage.completion_tokens;
          totalTokens.total += response.usage.total_tokens;

          // Update current token usage for real-time display
          this.currentTokenUsage = { ...totalTokens };
        }

        if (response.type === 'text') {
          // Track consecutive responses without tool calls
          this.consecutiveNoToolCalls++;

          // If we get 2 consecutive responses without tool calls, task is likely complete
          if (this.consecutiveNoToolCalls >= 2) {
            // Task appears complete
            this.clearProgress();
            this.sessionMemory.addMessages(messages);
            return {
              type: "completed",
              content: response.content,
              iterations,
              toolCalls,
              tokens: totalTokens,
              context: this.context
            };
          }
          // Check if the response contains inline function calls
          const functionCallPattern = /<function=([^>{\s]+)(\{[^}]+\})?[^>]*>/g;
          const matches = [...response.content.matchAll(functionCallPattern)];
          
          if (matches.length > 0) {
            // Extract and execute inline function calls
            for (const match of matches) {
              const functionName = match[1];
              const paramsStr = match[2];
              
              try {
                // Parse parameters
                let params = {};
                if (paramsStr && paramsStr.trim().startsWith('{')) {
                  params = JSON.parse(paramsStr);
                }
                
                // Create a pseudo tool call
                const toolCall = {
                  id: `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  type: 'function',
                  function: {
                    name: functionName,
                    arguments: JSON.stringify(params)
                  }
                };
                
                toolCalls++;
                
                // Execute the tool
                const result = await ErrorHandler.withQuickRetry(
                  () => this.executeTool(toolCall),
                  `tool execution: ${functionName}`
                );
                
                // Add tool result to messages
                const toolMessage: Message = {
                  role: "tool",
                  content: JSON.stringify(result),
                  tool_call_id: toolCall.id,
                  name: functionName
                };
                messages.push(toolMessage);
                
                // Continue to next iteration to process the tool result
                continue mainLoop;
              } catch (error) {
                console.error(`Failed to parse/execute inline function call: ${match[0]}`);
              }
            }
          }
          
          // No function calls found or all processed - return final answer
          // Remove function call syntax from the response
          const cleanContent = response.content.replace(/<function=[^>]*>/g, '').replace(/<\/function>/g, '');

          // If tools were used but response lacks summary, generate one
          let finalContent = cleanContent.trim() || response.content;
          if (toolCalls > 0 && (!finalContent || finalContent.length < 50)) {
            finalContent = this.generateMissingSummary(toolCalls, iterations) + (finalContent ? '\n\n' + finalContent : '');
          }

          // Format markdown for terminal display
          finalContent = this.formatMarkdownForTerminal(finalContent);

          // Save conversation to session memory
          this.sessionMemory.addMessages(messages);

          // Automatically extract and remember facts (Railroad Memory)
          this.autoExtractMemories(task, finalContent, messages);

          return {
            type: "completed",
            content: finalContent,
            iterations,
            toolCalls,
            tokens: totalTokens,
            context: this.context
          };
        }

        if (response.type === 'tool_call' && response.tool_calls) {
          // Reset the no-tool-call counter since we're using tools
          this.consecutiveNoToolCalls = 0;

          // Agent wants to call tools
          const assistantMessage: Message = {
            role: "assistant",
            content: response.content,
            tool_calls: response.tool_calls
          };
          messages.push(assistantMessage);

          // Execute each tool call
          for (let idx = 0; idx < response.tool_calls.length; idx++) {
            const toolCall = response.tool_calls[idx];
            toolCalls++;

            // Check for duplicate operations to prevent loops
            // But be more lenient with read operations since they're often needed
            const operationKey = `${toolCall.function.name}:${toolCall.function.arguments}`;
            const recentCount = this.recentOperations.get(operationKey) || 0;
            const maxAttempts = ['read_file', 'list_files', 'git_status'].includes(toolCall.function.name) ? 6 : 3;

            if (recentCount >= maxAttempts) {
              // Silently skip without showing confusing message to user
              const errorResult = {
                success: false,
                error: `Please try a different approach to complete this task.`
              };
              const toolMessage: Message = {
                role: "tool",
                content: JSON.stringify(errorResult),
                tool_call_id: toolCall.id,
                name: toolCall.function.name
              };
              messages.push(toolMessage);
              continue;
            }

            // Track this operation
            this.recentOperations.set(operationKey, recentCount + 1);

            // Clean up old operations (keep last 10)
            if (this.recentOperations.size > 10) {
              const firstKey = this.recentOperations.keys().next().value;
              this.recentOperations.delete(firstKey);
            }

            // Show progress indicator
            const nextTool = response.tool_calls[idx + 1];
            this.showProgress(toolCall.function.name, nextTool?.function.name);

            // Display tool call in Claude Code format
            if (!this.context.permissionManager || this.context.permissionManager.getCurrentMode() !== PermissionMode.NORMAL) {
              // Only show tool calls when not in normal mode (approval dialogs handle display)
              console.log(kleur.gray(`\n<function=${toolCall.function.name}>\n${this.formatToolParams(toolCall.function.arguments)}\n</function>\n`));
            }

            const result = await ErrorHandler.withQuickRetry(
              () => this.executeTool(toolCall),
              `tool execution: ${toolCall.function.name}`
            );

            // Display tool result in a clean format like Claude Code
            this.displayToolResult(toolCall.function.name, result);

            const toolMessage: Message = {
              role: "tool",
              content: JSON.stringify(result),
              tool_call_id: toolCall.id,
              name: toolCall.function.name
            };
            messages.push(toolMessage);
          }
          
          continue; // Continue the loop for next iteration
        }

      } catch (error: any) {
        console.error(`\nError in iteration ${i + 1}:`, error.message);

        // Check if this is a recoverable API error
        if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
          console.log(kleur.yellow('\nAPI temporarily unavailable. Waiting before retry...'));
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Skip this iteration and try again
          if (i < maxIterations - 1) {
            continue;
          }
        }

        // Handle rate limits specifically
        if (error.message.includes('rate limit') || error.status === 429) {
          const waitTime = error.waitTime || 2000; // Use provided wait time or default to 2 seconds
          console.log(kleur.yellow(`\nRate limit reached. Waiting ${waitTime / 1000} seconds...`));
          await new Promise(resolve => setTimeout(resolve, waitTime));

          // Skip this iteration and try again
          if (i < maxIterations - 1) {
            continue;
          }
        }

        // For unrecoverable errors, return failure
        return {
          type: "failed",
          content: `Execution failed: ${error.message}`,
          iterations,
          toolCalls,
          tokens: totalTokens,
          context: this.context
        };
      }
    }

    // Max iterations reached
    this.clearProgress();

    return {
      type: "max_iterations",
      content: "Task did not complete within the maximum number of iterations",
      iterations,
      toolCalls,
      tokens: totalTokens,
      context: this.context
    };
  }

  private async loadPersona(): Promise<void> {
    const loader = new AssetLoader();

    try {
      // Priority 1: Check for project-specific persona (.metis/persona.yaml)
      const projectPersona = await loader.loadProjectPersona();
      if (projectPersona) {
        this.persona = projectPersona;
        if (this.context.verboseEnabled) {
          console.log(`Persona: ${this.persona.name} (project-specific)`);
        }
        return;
      }

      // Priority 2: Use environment variable or default persona
      const personaName = process.env.METIS_PERSONA || 'default';
      this.persona = await loader.loadPersona(personaName);
      if (this.context.verboseEnabled) {
        console.log(`Persona: ${this.persona.name} (${personaName === 'default' ? 'default' : 'environment'})`);
      }
    } catch (error: any) {
      // Fallback to default
      this.persona = {
        name: 'default',
        version: '1.0',
        description: 'Default persona',
        system_prompt: 'You are a helpful coding assistant with access to tools.',
        temperature: 0.2
      };
      if (this.context.verboseEnabled) {
        console.log('Persona: default (fallback)');
      }
    }
  }

  private getAvailableTools(requestedTools: string[]): FunctionDefinition[] {
    const allToolNames = toolRegistry.list();
    const toolsToUse = requestedTools.length > 0 ? requestedTools : allToolNames;

    const tools: FunctionDefinition[] = [];

    // Prioritize essential tools like read_file to encourage their use
    const priorityTools = ['read_file', 'write_file', 'edit_file'];
    const otherTools = toolsToUse.filter(t => !priorityTools.includes(t));
    const orderedTools = [...priorityTools.filter(t => toolsToUse.includes(t)), ...otherTools];

    for (const toolName of orderedTools) {
      const tool = toolRegistry.get(toolName);
      if (tool) {
        tools.push({
          name: tool.name,
          description: tool.description,
          parameters: tool.schema || {}
        });
      }
    }

    if (this.context.verboseEnabled) {
      console.log(`Tools: ${tools.length} available`);
    }

    return tools;
  }

  private buildSystemPrompt(tools: FunctionDefinition[]): string {
    const repoSummary = summarizeRepo(60);
    const sessionSummary = this.sessionMemory.getSessionSummary();
    const basePrompt = this.persona?.system_prompt || "You are a helpful coding assistant.";

    // Check for Agent.md updates and load hierarchical instructions
    this.agentMemory.refreshIfNeeded();
    const projectInstructions = this.agentMemory.getCurrentProjectInstructions();
    const projectContext = this.agentMemory.generateProjectContext(false);

    // Check if we're in planning mode (use PlanModeManager for Claude Code-style plan mode)
    const isInPlanningMode = this.planModeManager.isEnabled();

    // Apply personality traits if available
    let personalityPrompt = '';
    if (this.persona?.personality) {
      const traits = this.persona.personality;
      personalityPrompt = `\n\nPERSONALITY TRAITS:`;

      if (traits.communication_style) {
        personalityPrompt += `\n- Communication Style: ${traits.communication_style}`;
      }
      if (traits.explanation_depth) {
        personalityPrompt += `\n- Explanation Depth: ${traits.explanation_depth}`;
      }
      if (traits.code_review_tone) {
        personalityPrompt += `\n- Code Review Tone: ${traits.code_review_tone}`;
      }
      if (traits.help_approach) {
        personalityPrompt += `\n- Help Approach: ${traits.help_approach}`;
      }
      if (traits.humor_level) {
        personalityPrompt += `\n- Humor Level: ${traits.humor_level}`;
      }
      if (traits.formality) {
        personalityPrompt += `\n- Formality: ${traits.formality}`;
      }
      if (traits.encouragement) {
        personalityPrompt += `\n- Encouragement Level: ${traits.encouragement}`;
      }
    }

    // Apply behavior guidelines if available
    let behaviorPrompt = '';
    if (this.persona?.behavior && this.persona.behavior.length > 0) {
      behaviorPrompt = `\n\nBEHAVIOR GUIDELINES:`;
      this.persona.behavior.forEach(guideline => {
        behaviorPrompt += `\n- ${guideline}`;
      });
    }

    let systemPrompt = `${basePrompt}${personalityPrompt}${behaviorPrompt}

You have access to tools that allow you to read files, write files, check git status, and perform other operations to help complete coding tasks.

PLATFORM AWARENESS:
- Current platform: ${process.platform === 'win32' ? 'Windows' : process.platform === 'darwin' ? 'macOS' : 'Linux'}
- Use platform-appropriate commands:
  - Windows: del (not rm), dir (not ls), copy (not cp), move (not mv)
  - Unix/Mac: rm, ls, cp, mv
- Always use forward slashes in file paths for consistency
- The system will handle path conversion automatically

IMPORTANT BEHAVIORAL GUIDELINES:

1. EFFICIENT TOOL USAGE:
   - Only use tools when necessary to complete the user's request
   - Don't use list_files unless you need to understand project structure
   - Don't use read_file unless you need to see current file contents
   - For simple questions, respond directly without tool usage
   - Combine related operations when possible

2. SMART DECISION MAKING:
   - If user asks "create a file", just create it - don't list directories first
   - If user provides specific code, use it directly - don't read existing files unless needed
   - When user says "add X to file Y", go straight to editing if the addition is clear
   - Use read_file only when you need to understand existing code structure

3. CLEAN OUTPUT:
   - Be conversational and natural like Claude Code
   - Don't show intermediate steps unless they add value
   - Silent success for read operations - only show important results
   - Integrate tool usage seamlessly into responses

4. ALWAYS SUMMARIZE CHANGES:
   - After editing files, ALWAYS summarize what you changed
   - After creating files, briefly describe what you created
   - After making any modifications, explain the purpose and impact
   - Example: "I added a powerup system with 3 types: speed boost, size increase, and extra points"
   - Example: "I updated the config.py to reduce powerup spawn time from 300 to 180 seconds"
   - Be specific about what changed, not just "I made some changes"

5. WHEN TO USE TOOLS:
   - read_file: ALWAYS use this to read file contents (NOT grep or search tools)
   - edit_file: ALWAYS read the file FIRST with read_file, then edit
   - write_file: For creating new files or completely replacing content
   - list_files: Only when you need to understand project structure
   - grep/search: Only for finding patterns across multiple files
   - git_*: When user requests git operations or you're making commits

CRITICAL: When editing a specific file:
1. Use read_file to see the current contents
2. Use edit_file to make changes
NEVER use grep/search tools just to look at a single file's content!

Available tools:
${tools.map(tool => `- ${tool.name}: ${tool.description}`).join("\n")}

CRITICAL TOOL PARAMETER RULES:
- edit_file: Use ONLY "path", "search", "replace" parameters. NEVER use line_start, line_end, old_string, new_string
- multi_edit: Use ONLY "file_path" and "edits" array with "old_string", "new_string" in each edit
- write_file: Use ONLY "path" and "content" parameters
- read_file: Use ONLY "path" parameter (optionally line_start, line_end for partial reads)
- Follow the exact parameter names in tool schemas - do not invent or substitute parameters

PROJECT ANALYSIS RULES:
- When analyzing or summarizing projects for users, NEVER mention Agent.md files
- Agent.md is internal documentation for the AI agent - users don't need to see it
- Focus on actual project files: source code, configs, docs, tests, etc.
- If asked about project structure, exclude Agent.md from listings and summaries

MANDATORY TASK COMPLETION SUMMARY:
When you finish a task that involved using tools, you MUST provide a clear summary including:
- What files were created, modified, or deleted
- What specific changes were made
- The outcome or result of the work
- Any important notes or next steps
Example: "✅ Task completed! I modified 3 files: updated the API configuration in config.py, added error handling to main.py, and created a new utility function in utils.py. The changes implement proper logging and error handling as requested."`;

    if (isInPlanningMode) {
      // Use Claude Code-style plan mode prompt
      systemPrompt += this.planModeManager.getPlanModePrompt();
    } else {
      systemPrompt += `

IMPORTANT: Only use tools when the user explicitly asks for coding tasks, file operations, or technical work. For simple greetings, questions, or general conversation, respond normally without using tools.

PLAN MODE SUGGESTION:
When the user describes wanting to CREATE or BUILD something significant, use the suggest_plan_mode tool to offer Plan Mode. This is appropriate when:
- User says "I want to create/build/make a [new feature/app/system]"
- User describes a complex multi-file change
- User asks about implementing a new architecture
- The task would benefit from planning before coding

Do NOT suggest plan mode for:
- Simple file edits or bug fixes
- Questions or explanations
- Single-file changes
- When the user seems to want immediate action

When the user asks you to implement something or work with code:
1. ANALYZE the request to understand what needs to be done
2. For complex tasks, consider using suggest_plan_mode first
3. READ relevant files using read_file tool (NOT grep/search) to see current content
4. CHECK git status if needed to see what's already changed
5. IMPLEMENT the changes by writing/updating files directly
6. PROVIDE a clear summary of what you accomplished

CRITICAL FILE EDITING WORKFLOW:
- To edit a file: FIRST use read_file, THEN use edit_file
- To view a file: use read_file (NOT grep, NOT search)
- To find text across many files: use grep
- Never use grep/search just to read a single file!

Use tools appropriately - don't use them for casual conversation or when they're not needed.`;
    }

    systemPrompt += `

TODO MANAGEMENT - IMPORTANT:
You have access to todo management tools (create_todo, update_todo, list_todos, delete_todo, clear_completed_todos).
Use these proactively for ANY task that involves multiple steps or when the user specifically asks for todo/task management.

ALWAYS use todos when:
- User asks to "create a todo" or "make a todo list" or mentions "tasks"
- Multi-step implementation requests
- Complex feature development
- Planning or organizing work

Example workflow:
1. User: "Implement user authentication with login and signup"
2. You: Use create_todo for "Implement login functionality", create_todo for "Implement signup functionality", etc.
3. As you work: Use update_todo to mark items as "in_progress" then "completed"
4. Use list_todos to show progress to user

The user expects you to use these tools when appropriate!

Repository summary:
${repoSummary}`;

    // Add project-specific instructions from Agent.md files
    if (projectInstructions.trim()) {
      systemPrompt += `\n\n=== PROJECT-SPECIFIC INSTRUCTIONS ===\n${projectInstructions}`;
    }

    // Add project context information
    if (projectContext.trim()) {
      systemPrompt += `\n\nPROJECT CONTEXT:\n${projectContext}`;
    }

    // Add rules from .metis/rules/ directory
    const rulesContext = this.rulesLoader.generateRulesContext();
    if (rulesContext.trim()) {
      systemPrompt += `\n\n${rulesContext}`;
    }

    // Add Rosetta codebase context if available
    const hasRosetta = this.rosettaLoader.hasRosetta();
    if (hasRosetta) {
      const rosettaContext = this.rosettaLoader.generateContextPrompt();
      if (rosettaContext.trim()) {
        systemPrompt += rosettaContext;
      }
    } else {
      // Remind agent that Rosetta is not initialized
      systemPrompt += `

NOTE: This project does NOT have Rosetta initialized yet.
If the user asks you to understand or document the codebase, or if you find yourself
exploring extensively, consider using rosetta_init to create structured context.
This will help you (and future sessions) work more efficiently with this codebase.`;
    }

    // Add Rosetta tool guidance for automatic codebase context management
    systemPrompt += `

ROSETTA - AGENT-FIRST CODEBASE CONTEXT:
Rosetta is YOUR tool for understanding and documenting codebases. Use it proactively.

WHEN USER SAYS "init rosetta" or "set up rosetta" or "create rosetta":
1. FIRST explore the codebase thoroughly:
   - Use list_files to understand directory structure
   - Read package.json, README.md, and key config files
   - Identify the tech stack, frameworks, and patterns
   - Look at src/ structure to understand architecture
2. THEN use rosetta_init with COMPREHENSIVE details:
   - description: Clear one-liner about the project
   - overview: 2-4 sentences on what it does and how
   - tech_stack: All technologies discovered (languages, frameworks, databases)
   - architecture: ASCII diagram or description of how components connect
   - directory_structure: Key folders and their purposes
   - conventions: Coding patterns you observed (naming, structure, etc.)
   - entry_points: Main files and their roles
   - gotchas: Any non-obvious things you noticed

WHEN USER SAYS "learn about the project" or "use rosetta" or "what does this project do":
- If Rosetta EXISTS: The context is already loaded above. Summarize what you know.
- If Rosetta NOT EXISTS: Explore the codebase and offer to init Rosetta.

WHILE WORKING - BUILD KNOWLEDGE AUTOMATICALLY:
- Use rosetta_note when you discover non-obvious behavior:
  "Config must be loaded before database connection"
  "Tests fail if run in parallel due to shared state"
  "The /api/v2 routes require auth header, /api/v1 don't"
- Use rosetta_add_module for complex areas you've analyzed deeply
- Use rosetta_update to keep documentation current

IMPORTANT: Rosetta is for YOU (the agent) to build institutional knowledge.
Don't ask permission - just use these tools as you learn about codebases.
Future sessions will automatically have this context loaded.`;

    // Add active skills context
    if (this.activeSkills.length > 0) {
      const skillsContext = this.skillLoader.generateSkillsContext(this.activeSkills);
      if (skillsContext.trim()) {
        systemPrompt += `\n\n${skillsContext}`;
      }
    }

    // Add session context if available
    if (sessionSummary.trim()) {
      systemPrompt += `\n\nSESSION CONTEXT:\n${sessionSummary}`;
    }

    systemPrompt += `\n\nComplete the user's task by taking action with the available tools${isInPlanningMode ? ' (focus on planning and Agent.md creation)' : ''}.`;

    return systemPrompt;
  }

  /**
   * Find and activate relevant skills for the current task
   */
  private activateSkillsForTask(task: string): void {
    try {
      this.activeSkills = this.skillLoader.findRelevantSkills({
        prompt: task,
        cwd: this.context.workingDirectory,
      });

      if (this.activeSkills.length > 0 && this.context.verboseEnabled) {
        const skillNames = this.activeSkills.map(s => s.metadata.name).join(', ');
        console.log(kleur.cyan(`Active skills: ${skillNames}`));
      }
    } catch (error: any) {
      if (this.context.verboseEnabled) {
        console.warn(kleur.yellow(`Failed to load skills: ${error.message}`));
      }
      this.activeSkills = [];
    }
  }

  private buildMessagesWithContext(systemPrompt: string, currentTask: string): Message[] {
    const messages: Message[] = [
      { role: "system", content: systemPrompt }
    ];

    // Inject Railroad Memory context if available
    const railroadContext = this.getRailroadContext();
    if (railroadContext) {
      messages.push({
        role: "system",
        content: `## Persistent Memory (Railroad)\n${railroadContext}`
      });
    }

    // Add recent conversation history for context (but not too much to avoid token overload)
    const recentMessages = this.sessionMemory.getRecentConversation(4); // Last 4 messages

    if (recentMessages.length > 0) {
      // Skip system messages from history to avoid duplication
      const conversationHistory = recentMessages.filter(msg => msg.role !== "system");
      messages.push(...conversationHistory);
    }

    // Add current user message
    messages.push({ role: "user", content: currentTask });

    return messages;
  }

  /**
   * Get Railroad Memory context for injection
   */
  private getRailroadContext(): string | null {
    if (!this.railroadMemory || !this.railroadMemory.isInitialized()) {
      return null;
    }

    try {
      const prompt = this.railroadMemory.toPrompt();
      // Only include if there's meaningful content
      if (prompt.trim().length > 50) {
        return prompt;
      }
    } catch (error) {
      // Silently fail - Railroad is optional enhancement
    }

    return null;
  }

  /**
   * Extract and remember facts from a user message (called after processing)
   */
  async rememberFromMessage(userMessage: string, assistantResponse: string): Promise<void> {
    if (!this.railroadMemory || !this.railroadMemory.isInitialized()) {
      return;
    }

    try {
      // Simple extraction from user message
      this.railroadMemory.extractFromMessages([
        { role: 'user', content: userMessage }
      ]);

      // Update stats
      this.railroadMemory.incrementMessageCount();
    } catch (error) {
      // Silently fail - Railroad is optional enhancement
    }
  }

  /**
   * Automatically extract and remember facts from conversation (Railroad Memory)
   * This runs silently after each task completion
   */
  private autoExtractMemories(task: string, response: string, messages: Message[]): void {
    if (!this.railroadMemory || !this.railroadMemory.isInitialized()) {
      return;
    }

    try {
      // Extract from user messages
      const userMessages = messages.filter(m => m.role === 'user');
      this.railroadMemory.extractFromMessages(userMessages);

      // Track files that were modified (from tool calls)
      const toolMessages = messages.filter(m => m.role === 'tool');
      for (const toolMsg of toolMessages) {
        try {
          const content = typeof toolMsg.content === 'string' ? JSON.parse(toolMsg.content) : toolMsg.content;
          if (content?.metadata?.path && (toolMsg.name === 'write_file' || toolMsg.name === 'edit_file')) {
            this.railroadMemory.remember(`Modified file: ${content.metadata.path}`, { importance: 6 });
          }
        } catch {
          // Ignore parsing errors
        }
      }

      // Update context with current task summary
      if (task.length > 20) {
        this.railroadMemory.setContext(`Working on: ${task.substring(0, 100)}...`);
      }

      // Update stats
      this.railroadMemory.incrementMessageCount();
    } catch (error) {
      // Silently fail - Railroad is optional enhancement
    }
  }

  /**
   * Get Railroad Memory instance for external access
   */
  getRailroadMemory(): RailroadMemory | null {
    return this.railroadMemory;
  }

  private displayToolResult(toolName: string, result: ToolResult): void {
    // Clear any running progress indicator first
    this.clearProgress();

    // Only show results for operations that matter to the user
    // Silent operations: read_file, list_files, git_status (unless they fail)
    if (result.success) {
      const silentOps = ['read_file', 'list_files', 'list_directory', 'git_status', 'git_diff', 'git_log', 'grep', 'find_files'];

      if (silentOps.includes(toolName)) {
        // Silent success - don't show anything for successful read operations
        return;
      }

      // Show clean, minimal results for important operations
      this.showCleanToolResult(toolName, result);
    } else {
      // Show errors with helpful guidance
      this.showErrorWithGuidance(toolName, result);
    }
  }

  private showCleanToolResult(toolName: string, result: ToolResult): void {
    const path = result.metadata?.path || '';

    switch(toolName) {
      case 'write_file':
      case 'create_file':
        if (result.metadata?.created) {
          console.log(kleur.gray(`\nCreate(${path})`));
          if (result.content) {
            console.log(kleur.dim(`  → ${result.content}`));
          }
        } else {
          console.log(kleur.gray(`\nUpdate(${path})`));
          if (result.content) {
            console.log(kleur.dim(`  → ${result.content}`));
          }
        }
        break;

      case 'edit_file':
      case 'multi_edit':
        console.log(kleur.gray(`\nUpdate(${path})`));
        if (result.metadata?.changes) {
          console.log(kleur.dim(`  → Made ${result.metadata.changes} changes`));
        } else if (result.content) {
          console.log(kleur.dim(`  → ${result.content}`));
        }
        break;

      case 'git_add':
        // Silent success
        break;

      case 'git_commit':
        console.log(kleur.gray(`\nCommit created`));
        break;

      case 'bash':
      case 'run_command':
        if (result.content && result.content.trim()) {
          console.log(kleur.gray(`\nBash output:`));
          console.log(result.content);
        }
        break;

      case 'notebook_edit':
        console.log(kleur.gray(`\nNotebook(${path})`));
        break;

      default:
        // Generic success message for other tools
        if (result.content && typeof result.content === 'string' && result.content.length < 200) {
          console.log(kleur.gray(`\n${result.content}`));
        }
        break;
    }
  }

  private showErrorWithGuidance(toolName: string, result: ToolResult): void {
    const error = result.error || 'Unknown error';

    // Provide specific guidance based on common error patterns
    let guidance = '';

    if (error.includes('Path outside workspace')) {
      guidance = '\nTip: Try using a relative path within the current directory';
    } else if (error.includes('File not found') || error.includes('ENOENT')) {
      guidance = '\nTip: Check the file path spelling and ensure the file exists';
    } else if (error.includes('Permission denied') || error.includes('EACCES')) {
      guidance = '\nTip: You may not have permission to access this file/directory';
    } else if (error.includes('Directory not empty')) {
      guidance = '\nTip: Use a different directory or ensure it\'s empty first';
    } else if (error.includes('already exists')) {
      guidance = '\nTip: File already exists - use edit_file to modify or choose a different name';
    } else if (error.includes('Invalid notebook format')) {
      guidance = '\nTip: The file may be corrupted or not a valid Jupyter notebook';
    } else if (error.includes('Tool call validation failed')) {
      guidance = '\nTip: Check the parameters passed to the tool';
    } else if (error.includes('Command failed')) {
      guidance = '\nTip: The shell command encountered an error - check the command syntax';
    }

    console.log(kleur.red(`\nError: ${this.formatTaskName(toolName)} failed: ${error}${guidance}`));
  }

  private displayDiff(oldText: string, newText: string, lineNumber?: number): void {
    // Display a simple diff preview
    const maxLength = 80;
    const oldDisplay = oldText.length > maxLength ? oldText.substring(0, maxLength) + '...' : oldText;
    const newDisplay = newText.length > maxLength ? newText.substring(0, maxLength) + '...' : newText;

    if (lineNumber) {
      console.log(kleur.gray(`       ${lineNumber} `) + kleur.red(`- ${oldDisplay}`));
      console.log(kleur.gray(`       ${lineNumber} `) + kleur.green(`+ ${newDisplay}`));
    } else {
      // Show simplified diff for global replacements
      console.log(kleur.red(`       - ${oldDisplay}`));
      console.log(kleur.green(`       + ${newDisplay}`));
    }
  }

  private showProgress(currentTask: string, nextTask?: string): void {
    // Clear any existing progress indicator
    this.clearProgress();

    // Format the task name for display
    const taskDisplay = this.formatTaskName(currentTask);
    this.lastProgressLine = `${taskDisplay}...`;

    // Show the progress indicator with token counter (Metis-themed symbols)
    const symbols = ['⚡', '🔧', '🛠️', '⚙️'];
    let symbolIndex = 0;

    const showIndicator = () => {
      const tokenInfo = this.currentTokenUsage.total > 0
        ? kleur.cyan(` [${this.formatTokenCount(this.currentTokenUsage.total)} tokens]`)
        : '';

      const line = kleur.gray(`${symbols[symbolIndex]} ${taskDisplay}...`) + tokenInfo + kleur.gray(` (esc to interrupt · ctrl+t to show todos)`);
      process.stdout.write('\r' + ' '.repeat(100) + '\r' + line);
      symbolIndex = (symbolIndex + 1) % symbols.length;
    };

    // Initial display
    showIndicator();

    // Animate the symbol and update token count
    this.thinkingInterval = setInterval(showIndicator, 500);

    // Show next task if available
    if (nextTask) {
      console.log();
      console.log(kleur.gray(`  ⎿  Next: ${this.formatTaskName(nextTask)}`));
    }
  }

  private clearProgress(): void {
    if (this.thinkingInterval) {
      clearInterval(this.thinkingInterval);
      this.thinkingInterval = undefined;
      // Clear the line (extra space for token counter)
      process.stdout.write('\r' + ' '.repeat(100) + '\r');
    }
    this.lastProgressLine = '';
  }

  private formatTaskName(toolName: string): string {
    // Convert tool names to user-friendly descriptions
    const taskNames: Record<string, string> = {
      'write_file': 'Writing file',
      'read_file': 'Reading file',
      'edit_file': 'Editing file',
      'list_files': 'Listing files',
      'create_directory': 'Creating directory',
      'git_status': 'Checking git status',
      'git_commit': 'Creating commit',
      'bash': 'Executing command',
      'grep': 'Searching files',
      'find_files': 'Finding files',
      'create_todo': 'Creating todo',
      'update_todo': 'Updating todo',
      'list_todos': 'Listing todos'
    };

    return taskNames[toolName] || `Executing ${toolName}`;
  }

  private formatToolParams(paramsJson: string): string {
    try {
      const params = JSON.parse(paramsJson);
      // Format parameters cleanly, one per line
      return Object.entries(params)
        .map(([key, value]) => {
          const valueStr = typeof value === 'string' 
            ? value 
            : JSON.stringify(value, null, 2);
          return `${key}: ${valueStr}`;
        })
        .join('\n');
    } catch {
      return paramsJson;
    }
  }

  private async executeTool(toolCall: any): Promise<ToolResult> {
    const toolName = toolCall.function.name;
    const startTime = Date.now();

    try {
      const params = JSON.parse(toolCall.function.arguments);

      // === PLAN MODE CHECK ===
      if (this.planModeManager.isEnabled() && !this.planModeManager.isToolAllowed(toolName)) {
        return {
          success: false,
          error: this.planModeManager.getBlockedMessage(toolName),
          metadata: { blocked_by_plan_mode: true }
        };
      }

      // === PRE-TOOL HOOKS ===
      if (this.hookManager.isEnabled()) {
        try {
          const preHookResults = await this.hookManager.executePreToolHooks({
            tool_name: toolName,
            tool_input: params,
            tool_use_id: toolCall.id,
            session_id: this.context.sessionId,
          });

          // Check if any hook blocked the operation
          for (const result of preHookResults) {
            if (result.block) {
              return {
                success: false,
                error: result.blockMessage || 'Operation blocked by hook',
                metadata: { blocked_by_hook: true }
              };
            }
          }
        } catch (hookError: any) {
          if (this.context.verboseEnabled) {
            console.warn(kleur.yellow(`Pre-tool hook error: ${hookError.message}`));
          }
        }
      }

      // === CREATE CHECKPOINT for modifying operations ===
      const modifyingTools = ['write_file', 'edit_file', 'multi_edit', 'append_file', 'move_file', 'delete_file', 'git_commit', 'git_checkout', 'git_merge', 'git_rebase'];
      if (this.checkpointManager.isEnabled() && modifyingTools.includes(toolName)) {
        try {
          const targetFiles = this.extractTargetFiles(toolName, params);
          this.checkpointManager.createCheckpoint(
            `Before ${toolName}`,
            toolName,
            targetFiles,
            0 // conversation index - could track this better
          );

          if (this.context.verboseEnabled) {
            console.log(kleur.gray(`Checkpoint created before ${toolName}`));
          }
        } catch (cpError: any) {
          if (this.context.verboseEnabled) {
            console.warn(kleur.yellow(`Checkpoint error: ${cpError.message}`));
          }
        }
      }

      // Check file cache for read operations
      if (toolName === 'read_file' && params.file_path) {
        const cached = this.fileCache.get(params.file_path);
        if (cached && Date.now() - cached.timestamp < 60000) { // 1 minute cache
          return {
            success: true,
            content: cached.content,
            metadata: { path: params.file_path, cached: true }
          };
        }
      }

      // === EXECUTE TOOL ===
      const result = await toolRegistry.execute(
        toolName,
        params,
        this.context
      );

      const duration = Date.now() - startTime;

      // Cache file contents for read operations
      if (toolName === 'read_file' && result.success) {
        this.fileCache.set(params.file_path, {
          content: result.content,
          timestamp: Date.now()
        });

        // Limit cache size
        if (this.fileCache.size > 20) {
          const firstKey = this.fileCache.keys().next().value;
          this.fileCache.delete(firstKey);
        }
      }

      // Invalidate cache on write operations
      if (['write_file', 'edit_file', 'multi_edit'].includes(toolName)) {
        this.fileCache.delete(params.file_path || params.path);
      }

      // Track file operations in session memory
      this.trackFileOperations(toolName, params);

      // === POST-TOOL HOOKS ===
      if (this.hookManager.isEnabled()) {
        try {
          await this.hookManager.executePostToolHooks({
            tool_name: toolName,
            tool_input: params,
            tool_output: typeof result.content === 'string' ? result.content : JSON.stringify(result),
            tool_use_id: toolCall.id,
            session_id: this.context.sessionId,
            success: result.success,
            duration_ms: duration,
          });
        } catch (hookError: any) {
          if (this.context.verboseEnabled) {
            console.warn(kleur.yellow(`Post-tool hook error: ${hookError.message}`));
          }
        }
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        error: `Tool execution failed: ${error.message}`,
        metadata: {
          tool: toolName,
          error_type: error.constructor.name
        }
      };
    }
  }

  /**
   * Extract target files from tool parameters for checkpoint tracking
   */
  private extractTargetFiles(toolName: string, params: any): string[] {
    const files: string[] = [];

    if (params.path) files.push(params.path);
    if (params.file_path) files.push(params.file_path);
    if (params.from) files.push(params.from);
    if (params.to) files.push(params.to);

    // For multi_edit, extract all file paths
    if (toolName === 'multi_edit' && params.edits && Array.isArray(params.edits)) {
      for (const edit of params.edits) {
        if (edit.file_path) files.push(edit.file_path);
      }
    }

    return [...new Set(files)]; // Remove duplicates
  }

  private trackFileOperations(toolName: string, params: any): void {
    // Track files that are being worked on
    const fileTools = ['read_file', 'write_file', 'edit_file', 'append_to_file'];
    
    if (fileTools.includes(toolName) && params.path) {
      this.sessionMemory.addWorkingFile(params.path);
    } else if (toolName === 'move_file') {
      if (params.from) this.sessionMemory.addWorkingFile(params.from);
      if (params.to) this.sessionMemory.addWorkingFile(params.to);
    }
  }

  // Permission system methods
  getPermissionManager(): PermissionManager {
    return this.permissionManager;
  }

  getCurrentPermissionMode(): PermissionMode {
    return this.permissionManager.getCurrentMode();
  }

  setPermissionMode(mode: PermissionMode): void {
    this.permissionManager.setMode(mode);
  }

  private formatTokenCount(count: number): string {
    if (count < 1000) return count.toString();
    if (count < 10000) return `${(count / 1000).toFixed(1)}k`;
    return `${Math.round(count / 1000)}k`;
  }

  private formatMarkdownForTerminal(text: string): string {
    // Convert markdown formatting to terminal colors
    return text
      // Bold text: **text** or **`code`** -> bright white
      .replace(/\*\*`([^`]+)`\*\*/g, kleur.cyan().bold('$1'))
      .replace(/\*\*([^*]+)\*\*/g, kleur.white().bold('$1'))
      // Inline code: `code` -> cyan
      .replace(/`([^`]+)`/g, kleur.cyan('$1'))
      // List items: - item -> • item (with proper spacing)
      .replace(/^- (.+)$/gm, kleur.gray('•') + ' $1')
      // Replace checkmark for consistency
      .replace(/✅/g, '🚀');
  }

  private generateMissingSummary(toolCalls: number, iterations: number): string {
    const toolText = toolCalls === 1 ? '1 operation' : `${toolCalls} operations`;
    const stepText = iterations === 1 ? '1 step' : `${iterations} steps`;

    return `🚀 Task completed! I performed ${toolText} across ${stepText} to implement the requested changes. The files have been updated successfully.`;
  }

  cleanup(): void {
    this.clearProgress();
    this.permissionManager.close();
  }

  cyclePermissionMode(): PermissionMode {
    return this.permissionManager.cycleMode();
  }

  getPermissionModeDisplay(): string {
    return this.permissionManager.getModeDisplay();
  }

  // Plan mode methods (Claude Code-style)
  getPlanModeManager(): PlanModeManager {
    return this.planModeManager;
  }

  isPlanModeEnabled(): boolean {
    return this.planModeManager.isEnabled();
  }

  enablePlanMode(): void {
    this.planModeManager.enable();
  }

  disablePlanMode(): void {
    this.planModeManager.disable();
  }

  togglePlanMode(): boolean {
    return this.planModeManager.toggle();
  }

  // Persona management methods
  getPersona(): Persona | null {
    return this.persona;
  }

  async switchPersona(personaName: string): Promise<void> {
    const { AssetLoader } = await import('../assets/loader');
    const loader = new AssetLoader();

    try {
      // Try to load the specified persona
      const newPersona = await loader.loadPersona(personaName);
      this.persona = newPersona;

      if (this.context.verboseEnabled) {
        console.log(`✅ Persona switched to: ${this.persona.name}`);
      }
    } catch (error: any) {
      throw new Error(`Failed to load persona '${personaName}': ${error.message}`);
    }
  }

  async reloadPersona(): Promise<void> {
    const { AssetLoader } = await import('../assets/loader');
    const loader = new AssetLoader();

    try {
      // Priority 1: Try to load project-specific persona first
      const projectPersona = await loader.loadProjectPersona();
      if (projectPersona) {
        this.persona = projectPersona;
        if (this.context.verboseEnabled) {
          console.log(`✅ Persona reloaded: ${this.persona.name} (project-specific)`);
        }
        return;
      }

      // Priority 2: Use environment variable or default persona
      const personaName = process.env.METIS_PERSONA || 'default';
      this.persona = await loader.loadPersona(personaName);
      if (this.context.verboseEnabled) {
        console.log(`✅ Persona reloaded: ${this.persona.name} (${personaName === 'default' ? 'default' : 'environment'})`);
      }
    } catch (error: any) {
      // Fallback to default
      this.persona = {
        name: 'default',
        version: '1.0',
        description: 'Default persona',
        system_prompt: 'You are a helpful coding assistant with access to tools.',
        temperature: 0.2
      };

      if (this.context.verboseEnabled) {
        console.log(`⚠️ Failed to reload persona, using fallback: ${error.message}`);
      }
    }
  }
}